/* eslint-disable react/style-prop-object */
/* eslint-disable jsx-a11y/iframe-has-title */
import React from "react";
import { connect } from "react-redux";

import Dialog from "../dialog/Dialog";
import DialogClose from "../dialog/DialogClose";
import DialogContent from "../dialog/DialogContent";
import DialogFooter from "../dialog/DialogFooter";
import MessagingContact from "../../types/core/MessagingContact";
import MessageDetail from "../../types/system/MessageDetail";
import { IAppState } from "../../state/Store";
import { AuthJWTPayload } from "../../types/system/AuthJWT";
import AppState from "../../foundation/AppState";
import {
    fetchMessagingSummary,
    hasPush,
    openThread,
} from "../../state/system/Messaging";
import { setMessagingOpen } from "../../state/system/UIConfiguration";
import MessagingSidebar from "./MessagingSidebar";
import MessagingMain from "./MessagingMain";

import "./MessagingDialog.scss";

interface MessagingDialogProps {
    open: boolean;
    allowBroadcast: boolean;
    recipients: Array<MessagingContact>;
    openedMessage?: MessageDetail;
    threadIds: Array<number>;
    jwt: AuthJWTPayload;

    summaryStatus: string;
    threadStatus: string;

    initialiseMessagingSummary: () => void;
    setMessagingOpen: (value: boolean) => void;
    openThread: (userIds: number[]) => void;
    hasPush: (push: PushSubscription) => void;
}

interface MessagingDialogState {
    isOpen: boolean;
    isLoading: boolean;
    isDragged: boolean;
    isSystem: boolean;
    broadcasting: boolean;
    holdOpen: boolean;
    isBigEdit: boolean;
    isImageEdit: boolean;
    allowRegister: boolean;
}

class MessagingDialog extends React.Component<
    MessagingDialogProps,
    MessagingDialogState
> {
    state = {
        isOpen: false,
        isLoading: false,
        isDragged: false,
        isSystem: false,
        broadcasting: false,
        holdOpen: false,
        isBigEdit: false,
        isImageEdit: false,
        allowRegister: false,
    };

    componentDidMount(): void {
        this.initPush();
    }

    componentDidUpdate(
        prevProps: Readonly<MessagingDialogProps>,
        prevState: Readonly<MessagingDialogState>,
        snapshot?: any,
    ): void {
        if (!prevProps.open && this.props.open) {
            this.setState({
                isOpen: true,
                broadcasting: false,
                holdOpen: false,
                isLoading: true,
            });

            this.props.initialiseMessagingSummary();
        }

        if (
            prevProps.summaryStatus === "loading" &&
            this.props.summaryStatus === "succeeded"
        ) {
            this.setState({
                isLoading: false,
            });
        }

        if (prevProps.threadIds !== this.props.threadIds)
            this.props.openThread(this.props.threadIds);
    }

    static getDerivedStateFromProps(
        props: MessagingDialogProps,
        state: MessagingDialogState,
    ): Partial<MessagingDialogState> {
        return {
            isSystem:
                props.jwt?.sub !== "1" &&
                props.openedMessage?.Message?.UserID === 1,
        };
    }

    async initPush() {
        const registration = await navigator.serviceWorker.ready;

        if (registration.pushManager) {
            const subscription =
                await registration.pushManager.getSubscription();
            const registered = !!subscription
                ? await this.props.hasPush(subscription)
                : false;
            this.setState({ allowRegister: !registered });
        }
    }

    setIsLoading(value: boolean) {
        this.setState(() => ({
            isLoading: value,
        }));
    }

    isLoading(): boolean {
        return this.state.isLoading || this.props.threadStatus === "loading";
    }

    bodyChanged(html: string) {
        this.setState({
            isBigEdit:
                !this.disableText() &&
                !!html &&
                (html.indexOf("<br>") >= 0 || html.indexOf("</p><p>") >= 0) &&
                html !== "<p><br></p>",
            isImageEdit:
                !this.disableText() &&
                !!html &&
                html.indexOf('<p><img src="data:image/jpeg;base64') === 0,
        });
    }

    disableText(): boolean {
        return !(this.props.recipients?.length > 0) && !this.state.broadcasting;
    }

    messageOpen(): boolean {
        return !!this.props.openedMessage;
    }

    broadcast(): void {
        this.setState(prevState => ({
            broadcasting: !prevState.broadcasting,
        }));
    }

    render(): React.ReactNode {
        let classes = "message-dialog flex";
        classes += this.state.isBigEdit ? " big-edit" : "";
        classes += this.state.isImageEdit ? " image-edit" : "";
        classes += this.disableText() ? " disable-text" : "";
        classes += this.props.allowBroadcast ? " allow-broadcast" : "";
        classes += this.state.broadcasting ? " broadcast" : "";
        classes += this.messageOpen() ? " open" : "";
        classes += this.state.holdOpen ? " hold-open" : "";

        return (
            <>
                <Dialog
                    dialogTitle="Messaging"
                    dialogIcon="chat_bubble"
                    showDialog={this.props.open}
                    showLoading={this.isLoading()}
                    width={850}
                    zIndex={99}>
                    <DialogContent className={classes}>
                        <MessagingSidebar
                            broadcasting={this.state.broadcasting}
                            allowBroadcast={this.props.allowBroadcast}
                            allowRegister={this.state.allowRegister}
                            broadcast={() => this.broadcast()}
                            didSubscribe={() =>
                                this.setState({ allowRegister: false })
                            }
                        />
                        <MessagingMain
                            isSystem={this.state.isSystem}
                            broadcasting={this.state.broadcasting}
                            allowBroadcast={this.props.allowBroadcast}
                            broadcast={() => this.broadcast()}
                            onChange={this.bodyChanged.bind(this)}
                        />
                    </DialogContent>
                    <DialogFooter>
                        <DialogClose
                            onClick={() =>
                                this.props.setMessagingOpen(false)
                            }></DialogClose>
                    </DialogFooter>
                </Dialog>
            </>
        );
    }
}

const mapStateToProps = (state: IAppState) => {
    const { messaging, ui } = state.db;
    return {
        open: ui.messagingOpen,
        allowBroadcast: AppState.jwt?.isAdmin,
        recipients: messaging.recipients,
        openedMessage: messaging.openedMessage,
        summaryStatus: messaging.summaryStatus,
        threadStatus: messaging.threadStatus,
        threadIds: messaging.threadIds,
        jwt: AppState.jwt,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        initialiseMessagingSummary: () => dispatch(fetchMessagingSummary()),
        setMessagingOpen: (value: boolean) => dispatch(setMessagingOpen(value)),
        openThread: (userIds: number[]) => dispatch(openThread(userIds)),
        hasPush: (push: PushSubscription) => dispatch(hasPush(push)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(MessagingDialog);
